'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useContext } from 'react'

import { generateImageKitURL } from '@/lib/generate-imagekit-url'
import { ConvertContext } from '@/app/providers'
import {
  trackBusinessContactViewed,
  trackBusinessProfileViewed,
  trackBusinessSocialViewed,
  trackBusinessWebsiteViewed,
} from '@/lib/convert/conversions/business-directory'

import { clearUrlProtocol } from '@/lib/utils/url'
import { decodeSafeEntities, stripAllTags } from '@/lib/sanitise-strings'

import type { Hit } from 'instantsearch.js'

export default function CompanyCard({ item }: { item: Hit }) {
  const { sdkContext } = useContext(ConvertContext)

  const handleProfileViewed = () => {
    trackBusinessProfileViewed(sdkContext)
  }

  const handleSocialViewed = () => {
    trackBusinessSocialViewed(sdkContext)
  }

  const handleWebsiteViewed = () => {
    trackBusinessWebsiteViewed(sdkContext)
  }

  const handleContactViewed = () => {
    trackBusinessContactViewed(sdkContext)
  }

  const hasSocialMedia =
    item?.social_media?.linkedin ||
    item?.social_media?.instagram ||
    item?.social_media?.twitter ||
    item?.social_media?.facebook ||
    false

  return (
    <li className="grid grid-cols-[220px,1fr,30%] gap-x-m rounded-md border border-border bg-white px-m py-s @container/grid-item m:grid-cols-1 m:justify-start m:gap-y-m ms:p-s s:gap-y-s m-up:items-center">
      <div className="w-full">
        {item?.business_info?.logo_image && item?.permalink ? (
          <Link
            href={item.permalink}
            onClick={handleProfileViewed}
            className="inline-flex w-full max-w-[130px] items-center align-top s:h-[85px] s:max-w-[70%] m-up:aspect-square m-up:justify-center">
            <Image
              src={generateImageKitURL(item.business_info.logo_image, {
                trim: '40',
                height: '240',
                cropMode: 'pad_resize',
                focus: 'center',
              })}
              height={240}
              width={240}
              alt={item.business_info.logo_image_alt}
              className="block h-[85px] w-auto max-w-[min(170px,100%)] object-contain"
            />
          </Link>
        ) : null}
      </div>

      <div className="s:flex s:flex-col s:gap-xxs">
        <span className="h4 s:h6 mb-xxs s:mb-0">
          {item.post_title && item.permalink ? (
            <Link
              href={item.permalink}
              onClick={handleProfileViewed}
              className="duration-300 hover:text-primary">
              {decodeSafeEntities(item.post_title)}
            </Link>
          ) : null}
        </span>

        {item?.introduction ? (
          <p className="line-clamp-3 hidden text-17 @[240px]/grid-item:!line-clamp-3 @[240px]/grid-item:block">
            {decodeSafeEntities(stripAllTags(item.introduction))}
          </p>
        ) : null}
      </div>

      <ul className="s:gap-x text-black m-up:text-right [&_li]:text-17 m:[&_li]:text-16 s:[&_li]:text-15">
        {item.business_info.telephone ? (
          <li>
            <Link
              href={'tel:' + item.business_info.telephone}
              onClick={handleContactViewed}
              className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-primary duration-300 hover:text-black">
              {item.business_info.telephone}
            </Link>
          </li>
        ) : null}

        {item.business_info.email ? (
          <li>
            <Link
              href={'mailto:' + item.business_info.email}
              onClick={handleContactViewed}
              className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-primary duration-300 hover:text-black">
              {item.business_info.email}
            </Link>
          </li>
        ) : null}

        {item.business_info.website ? (
          <li>
            <Link
              href={item.business_info.website}
              onClick={handleWebsiteViewed}
              className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-primary duration-300 hover:text-black"
              target="_blank"
              rel="noopener noreferrer">
              {clearUrlProtocol(item.business_info.website)}
            </Link>
          </li>
        ) : null}

        {hasSocialMedia ? (
          <div className="mt-m flex items-center gap-xxs">
            <div className="flex w-full flex-col gap-s">
              <div className="flex gap-xs m-up:justify-end">
                {item?.social_media?.linkedin ? (
                  <Link
                    href={item.social_media.linkedin}
                    target="_blank"
                    onClick={handleSocialViewed}
                    className="hover:text-primary">
                    <svg className="h-24 w-24" aria-hidden="true">
                      <use href="/img/sprite.svg#icon-linkedin"></use>
                    </svg>
                  </Link>
                ) : null}

                {item?.social_media?.instagram ? (
                  <Link
                    href={item.social_media.instagram}
                    target="_blank"
                    onClick={handleSocialViewed}
                    className="hover:text-primary">
                    <svg className="h-24 w-24" aria-hidden="true">
                      <use href="/img/sprite.svg#icon-instagram"></use>
                    </svg>
                  </Link>
                ) : null}

                {item?.social_media?.twitter ? (
                  <Link
                    href={item.social_media.twitter}
                    target="_blank"
                    onClick={handleSocialViewed}
                    className="hover:text-primary">
                    <svg className="h-24 w-24" aria-hidden="true">
                      <use href="/img/sprite.svg#icon-x"></use>
                    </svg>
                  </Link>
                ) : null}

                {item?.social_media?.facebook ? (
                  <Link
                    href={item.social_media.facebook}
                    target="_blank"
                    onClick={handleSocialViewed}
                    className="hover:text-primary">
                    <svg className="h-24 w-24" aria-hidden="true">
                      <use href="/img/sprite.svg#icon-facebook"></use>
                    </svg>
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </ul>

      <Link
        href={item.permalink}
        onClick={handleProfileViewed}
        className="btn btn--solid btn--small mt-auto w-full justify-self-start @[300px]/grid-item:w-auto m-up:hidden">
        View Profile
      </Link>
    </li>
  )
}
